// Recompute an invoice's line items from time records, mirroring Android's
// BuildInvoiceLineItems. For each selected project we sum human-tracked and
// AI-tracked time (ms) within the period, convert to hours, and emit one line
// per tracker kind: "<project>" (human) and "<project> (AI)" (AI, only when
// aiRate > 0). Period end is inclusive of the whole picked day.

const MILLIS_PER_HOUR = 3_600_000;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @param {Object} args
 * @param {Array} args.timeRecords  all time records (FirebaseContext.allTimeRecords)
 * @param {Array} args.tasks        all tasks (to map taskId -> projectId)
 * @param {Array} args.projects     all projects (for names)
 * @param {Array<number>} args.projectIds  selected project ids
 * @param {number} args.periodStart epoch ms (start, inclusive)
 * @param {number} args.periodEnd   epoch ms (end day, inclusive of the whole day)
 * @param {number} args.humanRate   hourly rate for human work
 * @param {number} args.aiRate      hourly rate for AI work (0 disables AI lines)
 * @returns {Array<{description:string, quantityHours:number, unitPrice:number, amount:number}>}
 */
export function buildInvoiceLineItems({
  timeRecords = [],
  tasks = [],
  projects = [],
  projectIds = [],
  periodStart = 0,
  periodEnd = 0,
  humanRate = 0,
  aiRate = 0,
}) {
  const periodEndInclusive = periodEnd + DAY_MS - 1;
  const taskToProject = new Map(tasks.map((t) => [Number(t.id), Number(t.projectId)]));
  const projectById = new Map(projects.map((p) => [Number(p.id), p]));

  const lines = [];
  projectIds.map(Number).forEach((projectId) => {
    const project = projectById.get(projectId);
    if (!project) return;

    const recordsForProject = timeRecords.filter((rec) => {
      const started = Number(rec.startedTime) || 0;
      return (
        started >= periodStart &&
        started <= periodEndInclusive &&
        taskToProject.get(Number(rec.taskId)) === projectId
      );
    });

    const humanMs = recordsForProject
      .filter((r) => (r.trackerKind || "human") === "human")
      .reduce((s, r) => s + (Number(r.spentTime) || 0), 0);
    const aiMs = recordsForProject
      .filter((r) => r.trackerKind === "ai")
      .reduce((s, r) => s + (Number(r.spentTime) || 0), 0);

    const name = project.projectName || "Untitled project";

    if (humanMs > 0) {
      const hours = humanMs / MILLIS_PER_HOUR;
      lines.push({
        description: name,
        quantityHours: hours,
        unitPrice: humanRate,
        amount: hours * humanRate,
      });
    }
    if (aiMs > 0 && aiRate > 0) {
      const hours = aiMs / MILLIS_PER_HOUR;
      lines.push({
        description: `${name} (AI)`,
        quantityHours: hours,
        unitPrice: aiRate,
        amount: hours * aiRate,
      });
    }
  });
  return lines;
}
